import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Container, Paper, ThemeProvider, Typography } from '@mui/material'
import gaiaTheme from '../../../root-components/theme'
import httpClient from '../../../shared/services/http/http-client'
import { SrsDocument } from '../../../components/presentation-components/reporting-components/srs/srs-document'
import { ISrsPublicReport } from '../../../shared/services/srs-report/srs-report.model'

/**
 * Read-only public view of a shared UK SRS climate disclosure, rendered from the figures frozen when
 * the report was generated, never live data. Reachable without signing in, so it holds nothing
 * beyond what the organisation chose to publish. The document itself is SrsDocument, shared with the
 * signed-in report view.
 */
export const PublicSrsPage = () => {
  const { shareId } = useParams()
  const [report, setReport] = useState<ISrsPublicReport | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!shareId) return
    httpClient.get(`public-report/srs/${shareId}`)
      .then((response) => setReport(response.data))
      .catch((err) => {
        console.log(err)
        setError(true)
      })
  }, [shareId])

  if (error) {
    return (
      <ThemeProvider theme={gaiaTheme}>
        <Container maxWidth='sm' sx={{ mt: 6 }}>
          <Paper variant='outlined' sx={{ p: 4 }}>
            <Typography variant='h5' sx={{ mb: 2 }}>Not available</Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              This climate disclosure is no longer available. The link may have been disabled.
            </Typography>
          </Paper>
        </Container>
      </ThemeProvider>
    )
  }

  if (!report) return null

  return (
    <ThemeProvider theme={gaiaTheme}>
      <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh', py: 5 }}>
        <Container maxWidth='md'>
          <SrsDocument
            report={{
              organisationName: report.organisationName,
              organisationLogoUrl: report.organisationLogoUrl,
              generatedAt: report.generatedAt,
              // Narrative fields arrive flat on the payload, keyed by field name.
              narratives: report as unknown as Record<string, string | null>,
              signOffName: report.signOffName,
              signOffTitle: report.signOffTitle,
              signOffDate: report.signOffDate,
              sectionContent: report.sectionContent,
              figures: report.figures,
              preparedWithText: report.preparedWithText,
              structure: report.structure || null,
            }}
          />
        </Container>
      </Box>
    </ThemeProvider>
  )
}
